import { useMutation, useQuery } from "@apollo/client";
import moment from "moment";
import { useState } from "react";

import PlatformSkillsModal from "@apps/account/components/skills/platform-skills-modal";
import SkillEditModal from "@apps/account/components/skills/skill-edit-modal";
import archiveSkillMutation from "@apps/account/graphql/archive-skill-mutation";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import GraphqlError from "@components/error/graphql-error";
import Loading from "@components/loading/loading";
import Placeholder from "@components/placeholder/placeholder";

import workspaceSkillsQuery from "../graphql/workspace-skills-query";

type SkillRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
  body: string;
  updated: string;
  canEdit: boolean;
  canArchive: boolean;
};

type Props = {
  organizationId: number;
};

const WorkspaceSkills: React.FC<Props> = ({ organizationId }) => {
  const label = useLabel();
  const workspaceRefetch = [
    { query: workspaceSkillsQuery, variables: { organizationId } },
  ];
  const { data, loading, error } = useQuery<any>(workspaceSkillsQuery, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId },
  });
  const [archive, { loading: archiving }] = useMutation<any>(
    archiveSkillMutation,
    { refetchQueries: workspaceRefetch },
  );
  const [editing, setEditing] = useState<{ open: boolean; skill?: SkillRow }>({
    open: false,
  });
  const [platformOpen, setPlatformOpen] = useState(false);

  const skills: SkillRow[] = data?.workspaceSkills ?? [];

  const handleArchive = async (skill: SkillRow) => {
    if (!window.confirm(`Are you sure you want to archive "${skill.name}"?`)) {
      return;
    }
    try {
      await archive({ variables: { id: skill.id } });
      successNotificationVar({ title: "Skill archived" });
    } catch (e) {
      errorNotificationVar({
        title: "Could not archive skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <div aria-label="Workspace skills section" className="pb-12">
      <SkillEditModal
        isOpen={editing.open}
        onClose={() => setEditing({ open: false })}
        organizationId={organizationId}
        refetchQueries={workspaceRefetch}
        initial={
          editing.skill
            ? {
                id: editing.skill.id,
                name: editing.skill.name,
                description: editing.skill.description,
                body: editing.skill.body,
              }
            : undefined
        }
      />

      <PlatformSkillsModal
        isOpen={platformOpen}
        onClose={() => setPlatformOpen(false)}
        organizationId={organizationId}
        refetchQueries={workspaceRefetch}
      />

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-medium text-gray-800">
          Workspace skills
        </h3>
        <div className="flex gap-2">
          <Button
            text="Browse platform skills"
            onClick={() => setPlatformOpen(true)}
          />
          <Button
            theme="primary"
            text="New skill"
            onClick={() => setEditing({ open: true })}
          />
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Workspace skills are shared with everyone in your{" "}
        {label("organization")}. Members can invoke them in Topicflow AI by
        typing <code className="bg-gray-100 px-1 rounded">/skill-name</code> in
        chat.
      </p>

      {error && skills.length === 0 ? (
        <GraphqlError
          error={error}
          whatDidNotWork="Workspace skills could not be loaded."
        />
      ) : loading && skills.length === 0 ? (
        <div className="flex justify-center mt-8">
          <Loading mini size="5" />
        </div>
      ) : skills.length === 0 ? (
        <Placeholder>
          No workspace skills yet. Click &quot;New skill&quot; to create one
          that&apos;s available to everyone in your {label("organization")}.
        </Placeholder>
      ) : (
        <div className="border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Skill</th>
                <th className="px-4 py-2 font-medium w-1" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {skills.map((skill) => {
                const options: DropdownOptionType[] = [
                  {
                    label: "Test in Topicflow AI",
                    onClick: () =>
                      window.open(
                        `/ask-ai?p=${encodeURIComponent(`/${skill.slug}`)}`,
                        "_blank",
                      ),
                  },
                ];
                if (skill.canEdit) {
                  options.push({
                    label: "Edit",
                    onClick: () => setEditing({ open: true, skill }),
                  });
                }
                if (skill.canArchive) {
                  options.push({ type: "divider" });
                  options.push({
                    label: "Archive",
                    textColor: "text-red-600",
                    disabled: archiving,
                    onClick: () => handleArchive(skill),
                  });
                }
                return (
                  <tr key={skill.id} aria-label={`Skill row ${skill.name}`}>
                    <td className="px-4 py-2 align-top">
                      <div className="font-mono text-blue-link">
                        /{skill.slug}
                      </div>
                      {skill.description && (
                        <div className="text-sm text-gray-600 mt-0.5">
                          {skill.description}
                        </div>
                      )}
                      {skill.updated && (
                        <div className="text-xs text-gray-400 mt-1">
                          Updated {moment(skill.updated).fromNow()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top whitespace-nowrap">
                      <div className="flex justify-end">
                        <Dropdown
                          options={options}
                          aria-label={`Skill actions for ${skill.name}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSkills;
